/**
 * Trade-pack visuals — turning a pack's `icon` / `color` STRINGS into real
 * React components and real Tailwind classes.
 *
 * A `ServiceCategoryDef` stores presentation as data (`icon: 'Zap'`,
 * `color: 'amber'`) because packs live in Firestore, where you can't store a
 * component reference or trust a class name. Both sides of that need a
 * lookup, and both have a trap:
 *
 * 1. **Tailwind purges what it can't see.** `text-${color}-400` compiles to
 *    nothing — the class never reaches the stylesheet and the element renders
 *    unstyled. Every class here is therefore written out in full, as a
 *    literal, so Tailwind's scanner finds it. Do not "simplify" these maps
 *    into template strings.
 *
 * 2. **Icons must be an explicit allow-list.** Importing lucide's full
 *    catalogue to look a name up dynamically costs ~270 KB
 *    (`src/data/symbol-icons/lucide-catalogue.generated.ts` is lazy-loaded for
 *    exactly that reason). These are named imports, so bundlers keep only
 *    what's listed.
 *
 * Both lookups FAIL SOFT: an unknown colour renders neutral, an unknown icon
 * renders a generic square. A pack authored with a typo looks plain — it
 * never renders a blank tile or throws.
 *
 * `src/__tests__/trade-pack-visuals.test.ts` asserts every icon and colour
 * used by every seed in `scripts/seeds/trade-packs/` resolves here, so a new
 * pack that reaches for something unsupported fails CI rather than shipping
 * grey.
 */

import {
  AirVent, AlertCircle, Antenna, Battery, Blinds, Cable, Camera, Car,
  CircuitBoard, ClipboardCheck, Cog, DoorOpen, Droplet, Fan, Flame, Gauge,
  Globe, HardHat, Home, Key, KeyRound, Lightbulb, Lock, Monitor, Network,
  Package, Paintbrush, Phone, Plug, Radio, Receipt, Router, Satellite,
  Shield, ShieldCheck, Siren, Snowflake, Speaker, Sun, Thermometer, Tv,
  Wifi, Wind, Wrench, Zap,
  Square, type LucideIcon,
} from 'lucide-react';

/** Icons a pack may name. Add here (and only here) to widen the vocabulary. */
const PACK_ICONS: Record<string, LucideIcon> = {
  AirVent, AlertCircle, Antenna, Battery, Blinds, Cable, Camera, Car,
  CircuitBoard, ClipboardCheck, Cog, DoorOpen, Droplet, Fan, Flame, Gauge,
  Globe, HardHat, Home, Key, KeyRound, Lightbulb, Lock, Monitor, Network,
  Package, Paintbrush, Phone, Plug, Radio, Receipt, Router, Satellite,
  Shield, ShieldCheck, Siren, Snowflake, Speaker, Sun, Thermometer, Tv,
  Wifi, Wind, Wrench, Zap,
};

/** Fallback for an icon name this build doesn't carry. */
export const FALLBACK_PACK_ICON: LucideIcon = Square;

/** Resolve a pack's `icon` string to a component. Never throws. */
export function packIcon(name: string | undefined): LucideIcon {
  if (!name) return FALLBACK_PACK_ICON;
  return PACK_ICONS[name] ?? FALLBACK_PACK_ICON;
}

/** True when a pack could name this icon and get something other than the
 *  fallback. Used by the seed test. */
export function isKnownPackIcon(name: string): boolean {
  return name in PACK_ICONS;
}

/** The classes a category's colour resolves to across the app's surfaces. */
export interface PackColorClasses {
  /** Icon/accent foreground, on a tinted background. */
  icon: string;
  /** Tinted fill for a selected tile. */
  selectedBg: string;
  /** Border for a selected tile. */
  selectedBorder: string;
  /** Card chrome — border + very light fill. */
  card: string;
  /** Body text tint that survives both themes. */
  text: string;
}

const NEUTRAL: PackColorClasses = {
  icon: 'text-theme-secondary',
  selectedBg: 'bg-surface-hover',
  selectedBorder: 'border-theme',
  card: 'border-theme bg-surface-card',
  text: 'text-theme-secondary',
};

/**
 * Full literal class strings per colour — see the Tailwind-purge note above.
 * Every colour any seeded pack uses must appear here.
 */
const PACK_COLORS: Record<string, PackColorClasses> = {
  blue: {
    icon: 'text-blue-400', selectedBg: 'bg-blue-500/20', selectedBorder: 'border-blue-400/50',
    card: 'border-blue-400/30 bg-blue-500/5', text: 'text-blue-600 dark:text-blue-300',
  },
  indigo: {
    icon: 'text-indigo-400', selectedBg: 'bg-indigo-500/20', selectedBorder: 'border-indigo-400/50',
    card: 'border-indigo-400/30 bg-indigo-500/5', text: 'text-indigo-600 dark:text-indigo-300',
  },
  emerald: {
    icon: 'text-emerald-400', selectedBg: 'bg-emerald-500/20', selectedBorder: 'border-emerald-400/50',
    card: 'border-emerald-400/30 bg-emerald-500/5', text: 'text-emerald-600 dark:text-emerald-300',
  },
  purple: {
    icon: 'text-purple-400', selectedBg: 'bg-purple-500/20', selectedBorder: 'border-purple-400/50',
    card: 'border-purple-400/30 bg-purple-500/5', text: 'text-purple-600 dark:text-purple-300',
  },
  amber: {
    icon: 'text-amber-400', selectedBg: 'bg-amber-500/20', selectedBorder: 'border-amber-400/50',
    card: 'border-amber-400/30 bg-amber-500/5', text: 'text-amber-600 dark:text-amber-300',
  },
  red: {
    icon: 'text-red-400', selectedBg: 'bg-red-500/20', selectedBorder: 'border-red-400/50',
    card: 'border-red-400/30 bg-red-500/5', text: 'text-red-600 dark:text-red-300',
  },
  teal: {
    icon: 'text-teal-400', selectedBg: 'bg-teal-500/20', selectedBorder: 'border-teal-400/50',
    card: 'border-teal-400/30 bg-teal-500/5', text: 'text-teal-600 dark:text-teal-300',
  },
  fuchsia: {
    icon: 'text-fuchsia-400', selectedBg: 'bg-fuchsia-500/20', selectedBorder: 'border-fuchsia-400/50',
    card: 'border-fuchsia-400/30 bg-fuchsia-500/5', text: 'text-fuchsia-600 dark:text-fuchsia-300',
  },
  // orange + slate were used by SERVICE_CONFIGS (ACCESS_CONTROL,
  // FEE_FOR_SERVICE) but missing from the per-file map this replaces, so those
  // two categories rendered neutral. Present now.
  orange: {
    icon: 'text-orange-400', selectedBg: 'bg-orange-500/20', selectedBorder: 'border-orange-400/50',
    card: 'border-orange-400/30 bg-orange-500/5', text: 'text-orange-600 dark:text-orange-300',
  },
  slate: {
    icon: 'text-slate-300', selectedBg: 'bg-slate-500/20', selectedBorder: 'border-slate-400/50',
    card: 'border-slate-400/30 bg-slate-500/5', text: 'text-slate-600 dark:text-slate-300',
  },
  cyan: {
    icon: 'text-cyan-400', selectedBg: 'bg-cyan-500/20', selectedBorder: 'border-cyan-400/50',
    card: 'border-cyan-400/30 bg-cyan-500/5', text: 'text-cyan-600 dark:text-cyan-300',
  },
  green: {
    icon: 'text-green-400', selectedBg: 'bg-green-500/20', selectedBorder: 'border-green-400/50',
    card: 'border-green-400/30 bg-green-500/5', text: 'text-green-600 dark:text-green-300',
  },
  lime: {
    icon: 'text-lime-400', selectedBg: 'bg-lime-500/20', selectedBorder: 'border-lime-400/50',
    card: 'border-lime-400/30 bg-lime-500/5', text: 'text-lime-600 dark:text-lime-300',
  },
  pink: {
    icon: 'text-pink-400', selectedBg: 'bg-pink-500/20', selectedBorder: 'border-pink-400/50',
    card: 'border-pink-400/30 bg-pink-500/5', text: 'text-pink-600 dark:text-pink-300',
  },
  rose: {
    icon: 'text-rose-400', selectedBg: 'bg-rose-500/20', selectedBorder: 'border-rose-400/50',
    card: 'border-rose-400/30 bg-rose-500/5', text: 'text-rose-600 dark:text-rose-300',
  },
  sky: {
    icon: 'text-sky-400', selectedBg: 'bg-sky-500/20', selectedBorder: 'border-sky-400/50',
    card: 'border-sky-400/30 bg-sky-500/5', text: 'text-sky-600 dark:text-sky-300',
  },
  violet: {
    icon: 'text-violet-400', selectedBg: 'bg-violet-500/20', selectedBorder: 'border-violet-400/50',
    card: 'border-violet-400/30 bg-violet-500/5', text: 'text-violet-600 dark:text-violet-300',
  },
  yellow: {
    icon: 'text-yellow-400', selectedBg: 'bg-yellow-500/20', selectedBorder: 'border-yellow-400/50',
    card: 'border-yellow-400/30 bg-yellow-500/5', text: 'text-yellow-600 dark:text-yellow-300',
  },
};

/** Resolve a pack's `color` string to classes. Unknown → neutral, never blank. */
export function packColor(color: string | undefined): PackColorClasses {
  if (!color) return NEUTRAL;
  return PACK_COLORS[color] ?? NEUTRAL;
}

/** True when a pack could name this colour and get something other than
 *  neutral. Used by the seed test. */
export function isKnownPackColor(color: string): boolean {
  return color in PACK_COLORS;
}
